import { useEffect, useRef, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { isMotionReduced } from "./motionPreference";
import { DURATION, EASE } from "./tokens";

/**
 * The main content fades and rises a few pixels when the page changes (a new path, not a filter change), so moving
 * between screens feels like one application. It does not remount the page (state and scroll are untouched) and uses
 * opacity and transform only. Skipped with reduced motion and on the first page of a visit.
 */
export function PageTransition({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const ref = useRef<HTMLDivElement | null>(null);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const el = ref.current;
    if (!el || isMotionReduced() || typeof el.animate !== "function") return;
    el.animate([{ opacity: 0, transform: "translateY(6px)" }, { opacity: 1, transform: "none" }], { duration: DURATION.base, easing: EASE.standard });
  }, [pathname]);
  return (
    <div ref={ref} data-testid="page-transition">
      {children}
    </div>
  );
}
